import { useMemo } from "react";
import useSWR from "swr";
import { swrJsonFetcher } from "@/hooks/swr/fetcher";
import { Empty, GridNote, Panel, SearchBox, SourceError, WalletLink } from "../components/Bits";
import { type Col, DataGrid } from "../components/DataGrid";
import { View } from "../components/Shell";
import { ViewHeader } from "../components/ViewHeader";
import { STATIC_DATA } from "../lib/data";
import { blankText, count, isoDay, pct0, pctSigned, toneClass, usd } from "../lib/format";
import { matches } from "../lib/markets";
import { useSearchParam } from "../lib/nav";

interface Candidate {
	address: string;
	name: string | null;
	x_username: string | null;
	markets: number | null;
	their_roi: number | null;
	copy_1m: number | null;
	copy_5m: number | null;
	copy_30m: number | null;
	copy_5m_ci_lo: number | null;
	copy_5m_ci_hi: number | null;
	pre_roi: number | null;
	avg_bet: number | null;
	main_category: string | null;
	biggest_market: string | null;
	is_reference?: boolean;
}

interface CopyDoc {
	generated_at: string;
	copy_window: { from: string; to: string };
	candidates: Candidate[];
}

const roi = (key: keyof Candidate, label: string, w: number, prio?: number, title?: string): Col<Candidate> => ({
	key,
	label,
	title,
	w,
	right: true,
	prio,
	sort: (r) => r[key] as number | null,
	cell: (r) => <span className={toneClass(r[key] as number | null)}>{pctSigned(r[key] as number | null, 0)}</span>,
});

const COLS: Col<Candidate>[] = [
	{ key: "w", label: "Wallet", w: 104, grow: 1, sort: (r) => r.name ?? r.address, csv: (r) => r.address, cell: (r) => <WalletLink address={r.address} label={r.name} /> },
	{ key: "c", label: "Category", w: 70, prio: 3, sort: (r) => r.main_category, cell: (r) => <span className="text-dim">{r.main_category ?? "–"}</span> },
	{ key: "m", label: "Mkts", title: "Markets in the backtest window", w: 38, right: true, prio: 4, sort: (r) => r.markets, cell: (r) => count(r.markets) },
	roi("their_roi", "Theirs", 56, 2, "The wallet's own ROI on these markets, with no delay"),
	roi("copy_1m", "Copy 1m", 56, 5, "ROI from copying each buy at the next trade 1 minute later"),
	// The low bound leads and matches the headline's weight: the middle alone flatters a noisy wallet.
	roi("copy_5m_ci_lo", "5m low 90%", 66, undefined, "Low end of the bootstrap 90% interval for the 5-minute copy ROI"),
	roi("copy_5m", "Copy 5m", 56, undefined, "ROI from copying each buy at the next trade 5 minutes later: the middle estimate"),
	roi("copy_5m_ci_hi", "5m high 90%", 70, 1, "High end of the bootstrap 90% interval for the 5-minute copy ROI"),
	roi("copy_30m", "Copy 30m", 60, 6, "ROI from copying each buy at the next trade 30 minutes later"),
	roi("pre_roi", "Pre", 48, 7, "ROI on markets that ended before the window: what you could have known when picking them"),
	{ key: "b", label: "Avg bet", title: "The wallet's average bet size", w: 56, right: true, prio: 8, sort: (r) => r.avg_bet, cell: (r) => usd(r.avg_bet) },
	{ key: "g", label: "Biggest market", title: "The market with the most money copied", w: 150, grow: 1, prio: 9, cell: (r) => <span className="text-dim" title={r.biggest_market ?? ""}>{r.biggest_market ?? "–"}</span> },
];

export function CopyCandidatesView() {
	const { data, error, isLoading, mutate } = useSWR<CopyDoc>("data/copy_candidates.json", swrJsonFetcher, STATIC_DATA);
	const [q, setQ] = useSearchParam();
	const rows = data?.candidates ?? [];
	const shown = useMemo(() => rows.filter((r) => matches(q, r.name, r.address, r.main_category, r.biggest_market)), [rows, q]);
	const holding = rows.filter((r) => (r.copy_5m_ci_lo ?? 0) > 0).length;

	const body = error && !data ? (
		<SourceError source="the copy-candidates snapshot" onRetry={() => void mutate()} />
	) : data && shown.length === 0 ? (
		<Empty>No wallets match.</Empty>
	) : undefined;

	return (
		<View>
			<ViewHeader
				title="copy candidates"
				meta={data ? `${holding} of ${rows.length} wallets: 90% interval above zero` : blankText(41)}
				// One sentence before and after load, so the grid does not move when the dates arrive.
				desc={`Backtest: buys on markets resolved ${data?.copy_window.from ?? blankText(10)} to ${data?.copy_window.to ?? blankText(10)}, re-priced at the next trade 1, 5 and 30 minutes later. Excludes your price impact and taker fees.`}
			>
				<SearchBox value={q} onChange={setQ} placeholder="search wallet, category" />
			</ViewHeader>
			<Panel>
				<DataGrid
					label="Copy candidates"
					cols={COLS}
					rows={shown}
					rowKey={(r) => r.address}
					initialSort={{ key: "copy_5m_ci_lo", dir: "desc" }}
					csvName="copy-candidates"
					copyValue={(r) => r.address}
					loading={isLoading && !data}
					body={body}
				/>
				<GridNote>snapshot {isoDay(data?.generated_at)} · past results, not a forecast</GridNote>
			</Panel>
		</View>
	);
}
